import Intl from '@fx-ui/intl';
import { get, isNil, assign, find, Region } from '@libs/shared';
import { SuiteId, VIP_ORDER } from '~/constants';
import { IOrderInfos, OrderClassification } from '~/interfaces';
import { regionUtils } from '~/utils/region';
import { getSuiteName } from './brand';

/**
 * 格式化订单明细信息
 *
 * @param product 订单明细
 * @param region
 */
export const formatOrderProduct = (product: any, region: Region) => {
    let info = '';
    switch (product.type) {
        case 'level':
            info = Intl.get(get(VIP_ORDER.PACK, product.value).name);
            break;
        case 'service':
            info = Intl.get(get(VIP_ORDER.SERVICE, product.value).name);
            break;
        case 'user':
            info = Intl.get('order.user', {
                value: regionUtils.formatThousandNumber({ value: product.value, region })
            });
            break;
        case 'corp_executive_addon':
            info = Intl.get('order.add.corp.executive');
            break;
        case 'data_addon':
            info = Intl.get('order.add.data', {
                value: regionUtils.formatThousandNumber({ value: product.value, region })
            });
            break;
        case 'form_data_addon':
            info = Intl.get('order.add.data.form', {
                value: regionUtils.formatThousandNumber({ value: product.value, region })
            });
            break;
        case 'data_api_addon':
            info = Intl.get('order.add.api');
            break;
        case 'upload_addon':
            info = Intl.get('order.add.upload', {
                value: regionUtils.formatThousandNumber({
                    value: product.value / VIP_ORDER.UPLOAD_ADD_UNIT,
                    region
                })
            });
            break;
        case 'aggregate_addon':
            info = Intl.get('order.add.aggregate', {
                value: regionUtils.formatThousandNumber({ value: product.value, region })
            });
            break;
        case 'aggregation_addon':
            info = Intl.get('order.add.old.aggregation', {
                value: regionUtils.formatThousandNumber({ value: product.value, region })
            });
            break;
        case 'form_aggregation_addon':
            info = Intl.get('order.add.aggregation');
            break;
        case 'data_backup_addon':
            info = Intl.get('order.add.backup', {
                value: regionUtils.formatThousandNumber({ value: product.value, region })
            });
            break;
        case 'managers_addon':
            info = Intl.get('order.add.manager', {
                value: regionUtils.formatThousandNumber({ value: product.value, region })
            });
            break;
        case 'etl_addon':
            info = Intl.get('order.add.etl', {
                value: regionUtils.formatThousandNumber({ value: product.value, region })
            });
            break;
        case 'trigger_addon':
            info = Intl.get('order.add.trigger', {
                value: regionUtils.formatThousandNumber({ value: product.value, region })
            });
            break;
        case 'automation_loop_node_addon':
            info = Intl.get('automation.node.type.loop');
            break;
        case 'sms_pack':
            info = Intl.get('order.add.sms', {
                value: regionUtils.formatThousandNumber({ value: product.count, region }),
                count: regionUtils.formatThousandNumber({ value: 1000, region })
            });
            break;
        case 'ocr_pack':
            info = Intl.get('order.add.ocr', {
                value: regionUtils.formatThousandNumber({ value: product.count, region }),
                count: regionUtils.formatThousandNumber({ value: 1000, region })
            });
            break;
        case 'sso_addon':
            info = Intl.get('order.add.sso');
            break;
        case 'crm_addon':
            info = Intl.get('app.category.crmTemplate');
            break;
        case 'corp_theme_addon':
            info = Intl.get('corp.theme');
            break;
        case 'custom_login_addon':
            info = Intl.get('order.add.customLogin');
            break;
        case 'bpa_addon':
            info = Intl.get('order.add.bpa', {
                value: regionUtils.formatThousandNumber({ value: product.value, region })
            });
            break;
        case 'corp_coop_addon':
            info = Intl.get('order.add.corp.coop', {
                value: regionUtils.formatThousandNumber({ value: product.value, region })
            });
            break;
        case 'coin':
            info = Intl.get('order.add.coin.count', {
                value: regionUtils.formatThousandNumber({ value: product.count, region })
            });
            break;
        case 'plugin': {
            const { price, name, days } = product;
            if (isNil(days)) {
                info = `${Intl.get('openPlatform.unit.brief')}${Intl.get('symbol.quote', {
                    content: product.name
                })}`;
            } else {
                const pack = Intl.get('plugin.subscription.plan.unit', {
                    price: regionUtils.formatThousandNumber({ value: price, region }),
                    days: regionUtils.formatThousandNumber({ value: days, region })
                });
                info = Intl.get('order.plugin.subscription.detail', {
                    name,
                    pack
                });
            }
            break;
        }
        case 'datasource': {
            const { price, name, days } = product;
            if (isNil(days)) {
                info = `${Intl.get('fxp.base.open.open_mkt.data_source.ttl')}${Intl.get(
                    'symbol.quote',
                    {
                        content: name
                    }
                )}`;
            } else {
                const pack = Intl.get('plugin.subscription.plan.unit', {
                    price: regionUtils.formatThousandNumber({ value: price, region }),
                    days: regionUtils.formatThousandNumber({ value: days, region })
                });
                info = Intl.get('order.data_source.subscription.detail', {
                    name,
                    pack
                });
            }
            break;
        }
        case 'platform_datasource': {
            const { name, price } = product;
            const days = product.count;
            if (isNil(days) || isNil(price)) {
                info = Intl.get('fxp.base.open.open_mkt.data_source_name.ttl', {
                    dataName: name || ''
                });
            } else {
                const pack = Intl.get('plugin.subscription.plan.unit', {
                    price: regionUtils.formatThousandNumber({ value: price, region }),
                    days: regionUtils.formatThousandNumber({ value: days, region })
                });
                info = Intl.get('fxp.base.open.open_mkt.data_source_plan_info.tip', {
                    name,
                    pack
                });
            }
            break;
        }
        case 'open_private_plugin_addon':
            info = Intl.get('order.add.private.plugin');
            break;
        case 'kms_addon':
            info = Intl.get('order.add.kms');
            break;
        case 'workbench_advanced_addon':
            info = Intl.get('workbench.custom.pro');
            break;
        case 'permission_management_addon':
            info = Intl.get('order.add.permission.query');
            break;
        case 'watermark_addon':
            info = Intl.get('order.add.watermark');
            break;
        case 'print_addon':
            info = Intl.get('order.add.print', {
                value: regionUtils.formatThousandNumber({ value: product.value, region })
            });
            break;
        case 'threshold_addon':
            info = Intl.get('order.add.threshold');
            break;
        case 'analytics_addon':
            info = getSuiteName(SuiteId.ANALYTICS);
            break;
        case 'analytics_editor_addon':
            info = Intl.get('data.analytics.editor', {
                value: regionUtils.formatThousandNumber({ value: product.value, region })
            });
            break;
        case 'analytics_viewer_addon':
            info = Intl.get('data.analytics.viewer', {
                value: regionUtils.formatThousandNumber({ value: product.value, region })
            });
            break;
        case 'corp_coop_docker_addon': {
            info = Intl.get('order.corp.coop.docker', {
                value: regionUtils.formatThousandNumber({ value: product.value, region })
            });
            break;
        }
        case 'form_hierarchy_view_addon':
            info = Intl.get('vip.view');
            break;
        default:
            break;
    }
    return info;
};

/**
 * 获取订单来源标签
 */
export const getSourceLabel = (source: OrderClassification) => {
    switch (source) {
        case OrderClassification.PLATFORM:
            return Intl.get('platform');
        case OrderClassification.APP_BUILDER:
            return getSuiteName(SuiteId.APP_BUILDER);
        case OrderClassification.ANALYTICS:
            return getSuiteName(SuiteId.ANALYTICS);
        case OrderClassification.GOVERNANCE:
            return getSuiteName(SuiteId.GOVERNANCE);
        case OrderClassification.SALES:
            return getSuiteName(SuiteId.SALES);
        case OrderClassification.WUFAN:
            return getSuiteName(SuiteId.WUFAN);
        default:
            return '';
    }
};

/**
 * 对订单产品进行分类
 */
export const classifyProducts = (orderInfo: IOrderInfos) => {
    return {
        [OrderClassification.OLD]: assign({}, orderInfo, {
            orderClassification: OrderClassification.OLD
        }),
        [OrderClassification.PLATFORM]: assign({}, orderInfo.platform, {
            orderClassification: OrderClassification.PLATFORM,
            orderInfo
        }),
        [OrderClassification.APP_BUILDER]: assign(
            {},
            find(orderInfo.suite, (item) => item.suite_id === SuiteId.APP_BUILDER),
            {
                orderClassification: OrderClassification.APP_BUILDER,
                orderInfo
            }
        ),
        [OrderClassification.ANALYTICS]: assign(
            {},
            find(orderInfo.suite, (item) => item.suite_id === SuiteId.ANALYTICS),
            {
                orderClassification: OrderClassification.ANALYTICS,
                orderInfo
            }
        ),
        [OrderClassification.GOVERNANCE]: assign(
            {},
            find(orderInfo.suite, (item) => item.suite_id === SuiteId.GOVERNANCE),
            {
                orderClassification: OrderClassification.GOVERNANCE,
                orderInfo
            }
        ),
        [OrderClassification.SALES]: assign(
            {},
            find(orderInfo.suite, (item) => item.suite_id === SuiteId.SALES),
            {
                orderClassification: OrderClassification.SALES,
                orderInfo
            }
        ),
        [OrderClassification.WUFAN]: assign(
            {},
            find(orderInfo.suite, (item) => item.suite_id === SuiteId.WUFAN),
            {
                orderClassification: OrderClassification.WUFAN,
                orderInfo
            }
        )
    };
};
